import { ScaleLinear } from 'd3-scale';
import classNames from 'classnames';

import { formatPropertyValue } from '@/explore/utils/format';

import { SeriesMetaData } from '../grouped-chart/types';
import { increaseBrightness } from '../utils';

import { CategoryScale, CategoryValue } from './types';

import styles from './series-values.module.scss';

const ChartEdgePadding = 4;

type SeriesValuesProps = {
  series: SeriesMetaData;
  data: { categoryValue: CategoryValue; value: number | null; cumulativeValue?: number }[];
  valueScale: ScaleLinear<number, number>;
  categoryScale: CategoryScale;
  x?: number;
  y?: number;
  avoidOverflow?: boolean;
  bar?: boolean;
};

export const SeriesValues = (props: SeriesValuesProps) => {
  const { data, series, valueScale, categoryScale, avoidOverflow = false, bar = false } = props;

  return data.map((d, i) => {
    if (d.value === null) {
      return null;
    }
    const isFirst = i === 0;
    const isLast = i === data.length - 1;
    const textAnchor =
      avoidOverflow && isFirst ? 'start' : avoidOverflow && isLast ? 'end' : 'middle';
    const dx =
      avoidOverflow && isFirst ? ChartEdgePadding : avoidOverflow && isLast ? -ChartEdgePadding : 0;
    const showValue = d.value !== 0;
    const x = (categoryScale(d.categoryValue.formatted) ?? 0) + categoryScale.bandwidth() / 2;
    const maxY = valueScale.range()[0];
    const y = Math.min(
      maxY - ChartEdgePadding,
      valueScale((d.cumulativeValue ?? d.value) + (bar ? d.value / 2 : 0)) ?? 0,
    );
    return showValue ? (
      <text
        key={i}
        className={classNames(styles.valueLabel, { [styles.white]: bar })}
        x={x}
        y={y}
        dx={dx + (props.x ?? 0)}
        dy={(bar ? 4 : -8) + (props.y ?? 0)}
        fill={increaseBrightness(series.color, 20)}
        style={{ textAnchor }}>
        {formatPropertyValue(d.value, {
          type: series.type,
          format: series.format,
          style: 'compact',
        })}
      </text>
    ) : null;
  });
};
